// src/components/EnhancedParticipantsWidget.tsx
import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Search, UserCircle, CheckCircle, ClipboardList } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ProcessItem, ParticipantWithResponsibility } from "@/types";
import { 
  getParticipantsForAlternative,
  hasAlternativeData
} from "@/utils/processUtils";

interface EnhancedParticipantsWidgetProps {
  processItem: ProcessItem;
  filteredRoles?: string[]; // Add filtered roles prop
}

const EnhancedParticipantsWidget: React.FC<EnhancedParticipantsWidgetProps> = ({ 
  processItem,
  filteredRoles = [] // Default to empty array if not provided
}) => {
  const [activeAlternative, setActiveAlternative] = useState<1 | 2>(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sideBySide, setSideBySide] = useState(false);  // State för side-by-side jämförelse
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Mobile detection
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Kontrollera om det finns data för båda alternativen
  const hasAlt1 = hasAlternativeData(processItem, 1);
  const hasAlt2 = hasAlternativeData(processItem, 2);
  const hasMultipleAlternatives = hasAlt1 && hasAlt2;
  
  // Om det aktiva alternativet inte har data, växla till det andra
  useEffect(() => {
    if ((activeAlternative === 1 && !hasAlt1 && hasAlt2) || 
        (activeAlternative === 2 && !hasAlt2 && hasAlt1)) {
      setActiveAlternative(activeAlternative === 1 ? 2 : 1);
    }
  }, [hasAlt1, hasAlt2, activeAlternative]);
  
  // Detect mobile view based on window size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Disable side-by-side on mobile
      if (window.innerWidth < 768 && sideBySide) {
        setSideBySide(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sideBySide]);
  
  // Hämta deltagare för det aktiva alternativet och alternativ för jämförelse
  const participantsWithResponsibilities = getParticipantsForAlternative(
    processItem, 
    activeAlternative
  );
  
  // Om side-by-side läge är aktivt, hämta även det andra alternativet
  const compareParticipants = hasMultipleAlternatives && sideBySide
    ? getParticipantsForAlternative(processItem, activeAlternative === 1 ? 2 : 1)
    : [];

  // Om det inte finns data för något alternativ, visa ingen widget
  if (!hasAlt1 && !hasAlt2) {
    return null;
  }
  
  // Filtrera deltagare baserat på sökterm
  const filteredParticipants = searchTerm
    ? participantsWithResponsibilities.filter(
        p => p.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
             p.responsibility.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : participantsWithResponsibilities;

  // Filtrera jämförelsedata med samma sökterm om side-by-side läge är aktivt
  const filteredCompareParticipants = searchTerm && sideBySide
    ? compareParticipants.filter(
        p => p.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
             p.responsibility.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : compareParticipants;

  // Hämta en konsekvent färg baserad på roll
  const getRoleColor = (role: string) => {
    const colors = [
      "bg-blue-400/20 text-blue-300 border-blue-400/30",
      "bg-green-400/20 text-green-300 border-green-400/30",
      "bg-purple-400/20 text-purple-300 border-purple-400/30",
      "bg-orange-400/20 text-orange-300 border-orange-400/30",
      "bg-teal-400/20 text-teal-300 border-teal-400/30",
      "bg-pink-400/20 text-pink-300 border-pink-400/30",
    ];
    
    // Använd roll för att deterministiskt välja en färg
    const charSum = role
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    
    return colors[charSum % colors.length];
  };

  // Få färger för checkmark-ikonerna baserat på rollen
  const getRoleIconColor = (role: string) => {
    const colorClass = getRoleColor(role);
    // Extrahera färgnamnet från klassen
    const colorMatch = colorClass.match(/text-(\w+)-\d+/);
    // Returnera färgen eller 'orange' som en säker fallback
    return colorMatch ? colorMatch[1] : 'orange';
  };

  // Konvertera ett Tailwind-färgnamn till en hex- eller RGB-färg
  const getRoleColorHex = (colorName: string) => {
    // Mappning från Tailwind färgnamn till hex-färger
    const colorMap: Record<string, string> = {
      blue: '#60a5fa',    // blue-400
      green: '#34d399',   // green-400
      purple: '#a78bfa',  // purple-400
      orange: '#fb923c',  // orange-400
      teal: '#2dd4bf',    // teal-400
      pink: '#f472b6',    // pink-400
      // Fallback-färg
      default: '#fb923c'  // orange-400
    };
    
    return colorMap[colorName] || colorMap.default;
  };

  // Check if a role is being filtered
  const isRoleFiltered = (role: string) => {
    return filteredRoles.includes(role);
  };

  // Derive role highlight style with custom color
  const getRoleHighlightColor = (role: string) => {
    if (!isRoleFiltered(role)) return "";
    
    // Extract color properties from the role's color class
    const colorClass = getRoleColor(role);
    // Detect border color from the class name
    const borderColorMatch = colorClass.match(/border-(\w+)-\d+\/\d+/);
    const colorName = borderColorMatch ? borderColorMatch[1] : 'green';
    
    // Use the same color for the highlight effect
    return `filtered-role-${colorName}`;
  };

  // Funktion för att rendera en punktlista av ansvar med animering
  const renderResponsibilityList = (responsibility: string, role: string) => {
    if (!responsibility) {
      return <span className="text-gray-500 italic">Inget specifikt ansvar</span>;
    }

    // Få färgkod baserat på rollen
    const roleColor = getRoleIconColor(role);
    
    // Skapa en direkt inline-stil med hex-färg
    const iconStyle = {
      color: getRoleColorHex(roleColor), // Konvertera färgnamn till hex
    };

    // Om ansvaret innehåller bulletpoints (•), rendera som en lista
    if (responsibility.includes('•')) {
      const items = responsibility.split('\n').map(item => item.startsWith('•') ? item.substring(1).trim() : item.trim()).filter(Boolean);
      
      return (
        <ul className="space-y-2 mt-1">
          {items.map((item, idx) => (
            <motion.li 
              key={idx} 
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="flex items-start gap-2"
            >
              <span className="flex-shrink-0 mt-0.5" style={iconStyle}>
                <CheckCircle className="h-3 w-3" />
              </span>
              <span className="text-gray-300 text-xs">{item}</span>
            </motion.li>
          ))}
        </ul>
      );
    }

    // För enstaka ansvar, visa utan lista
    return (
      <div className="mt-1 flex items-start gap-2">
        <span className="flex-shrink-0 mt-0.5" style={iconStyle}>
          <CheckCircle className="h-3 w-3" />
        </span>
        <span className="text-gray-300 text-xs">{responsibility}</span>
      </div>
    );
  };

  // Funktion för att rendera ett rollkort i kolumnlayout
  const renderRoleCard = (participant: ParticipantWithResponsibility, index: number) => {
    const colorClass = getRoleColor(participant.role);
    const isFiltered = isRoleFiltered(participant.role);
    
    return (
      <motion.div
        key={`${participant.role}-${index}`}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ delay: 0.03 * index }}
        className={`bg-gray-800/40 rounded-lg overflow-hidden border border-gray-700/50 h-full ${isFiltered ? 'filtered-role' : ''}`}
      >
        {/* Roll-header */}
        <div className={`p-2 flex items-center gap-2 ${isFiltered ? 'bg-gray-700/30' : ''} border-b border-gray-700/30`}>
          <div className={`w-1 h-4 rounded-sm self-stretch ${colorClass.split(' ')[0]} ${isFiltered ? 'animate-pulse' : ''}`}></div>
          <div className={`flex items-center gap-1.5 font-medium ${colorClass.split(' ')[1]} ${isFiltered ? 'font-bold' : ''}`}>
            <UserCircle className="h-4 w-4" />
            <span className="text-sm truncate">{participant.role}</span>
          </div>
        </div>
        
        {/* Ansvarsområden */}
        <div className="p-2">
          {participant.responsibility ? (
            renderResponsibilityList(participant.responsibility, participant.role)
          ) : (
            <span className="text-gray-500 italic block p-1 text-xs">Inget specifikt ansvar</span>
          )}
        </div>
      </motion.div>
    );
  };

  // Beräkna optimal kolumnbredd baserat på antal deltagare
  const getColumnClass = () => {
    const count = filteredParticipants.length;
    
    if (isMobile) {
      // På mobil alltid ha en kolumn för bättre visning
      return "grid-cols-1";
    }
    
    if (sideBySide) return "grid-cols-1"; // En kolumn per alternativ i jämförelseläge
    
    if (count <= 2) return "grid-cols-1 sm:grid-cols-2";
    if (count <= 3) return "grid-cols-1 sm:grid-cols-3";
    return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3";
  };

  return (
    <Card 
      className="mb-6 bg-gray-800/20 border-gray-700/50 backdrop-blur-md"
      ref={containerRef}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-orange-300" />
            <h3 className="text-lg font-medium text-white">Deltagare & ansvar</h3>
          </div>
          
          <div className="flex flex-wrap gap-2 items-center">
            {/* Sökfält */}
            {participantsWithResponsibilities.length > 3 && (
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Sök roller..."
                  className="w-36 sm:w-40 bg-gray-800/50 border border-gray-700 rounded-lg py-1 pl-8 pr-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-300/50"
                />
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              </div>
            )}
            
            {/* Alternativ-knappar och jämförelseläge - visa bara på större skärmar */}
            {hasMultipleAlternatives && (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex bg-gray-800/50 rounded-lg p-0.5 border border-gray-700">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`px-3 py-1 text-xs rounded-md ${
                      activeAlternative === 1 && !sideBySide
                        ? "bg-orange-300 text-gray-900 hover:bg-orange-400 focus:ring-0 focus:ring-offset-0"
                        : "bg-transparent text-gray-400 hover:text-white hover:bg-gray-700/50"
                    }`}
                    onClick={() => { 
                      setActiveAlternative(1);
                      setSideBySide(false);
                    }}
                  >
                    Alternativ 1
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`px-3 py-1 text-xs rounded-md ${
                      activeAlternative === 2 && !sideBySide
                        ? "bg-orange-300 text-gray-900 hover:bg-orange-400 focus:ring-0 focus:ring-offset-0"
                        : "bg-transparent text-gray-400 hover:text-white hover:bg-gray-700/50"
                    }`}
                    onClick={() => { 
                      setActiveAlternative(2);
                      setSideBySide(false);
                    }}
                  >
                    Alternativ 2
                  </Button>
                  {/* Dölj jämför-knappen på mobil */}
                  {!isMobile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`px-3 py-1 text-xs rounded-md ${
                        sideBySide
                          ? "bg-orange-300 text-gray-900 hover:bg-orange-400 focus:ring-0 focus:ring-offset-0"
                          : "bg-transparent text-gray-400 hover:text-white hover:bg-gray-700/50"
                      }`}
                      onClick={() => setSideBySide(!sideBySide)}
                    >
                      Jämför
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
       
        {/* Innehåll för deltagare och ansvar som kolumner */}
        {!sideBySide ? (
          // Standard kolumnlayout
          <div className={`grid ${getColumnClass()} gap-4`}>
            <AnimatePresence>
              {filteredParticipants.length > 0 ? (
                filteredParticipants.map((participant, index) => (
                  renderRoleCard(participant, index)
                ))
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 text-center text-gray-400 bg-gray-800/30 rounded-lg border border-gray-700/50 col-span-full"
                >
                  {searchTerm ? (
                    <>Inga deltagare matchar sökningen</>
                  ) : (
                    <>Inga deltagare har definierats för detta alternativ</>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          // Side-by-side jämförelselayout
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Alternativ 1 */}
            <div>
              <div className="bg-gray-800/70 p-2 mb-3 rounded-lg border border-gray-700/70">
                <h4 className="text-white font-medium">Alternativ 1</h4>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <AnimatePresence>
                  {(activeAlternative === 1 ? filteredParticipants : filteredCompareParticipants).length > 0 ? (
                    (activeAlternative === 1 ? filteredParticipants : filteredCompareParticipants).map((participant, index) => (
                      renderRoleCard(participant, index)
                    ))
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 text-center text-gray-400 bg-gray-800/30 rounded-lg border border-gray-700/50 col-span-full"
                    >
                      {searchTerm ? (
                        <>Inga deltagare matchar sökningen</>
                      ) : (
                        <>Inga deltagare har definierats för detta alternativ</>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            
            {/* Alternativ 2 */}
            <div>
              <div className="bg-gray-800/70 p-2 mb-3 rounded-lg border border-gray-700/70">
                <h4 className="text-white font-medium">Alternativ 2</h4>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <AnimatePresence>
                  {(activeAlternative === 2 ? filteredParticipants : filteredCompareParticipants).length > 0 ? (
                    (activeAlternative === 2 ? filteredParticipants : filteredCompareParticipants).map((participant, index) => (
                      renderRoleCard(participant, index)
                    ))
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 text-center text-gray-400 bg-gray-800/30 rounded-lg border border-gray-700/50 col-span-full"
                    >
                      {searchTerm ? (
                        <>Inga deltagare matchar sökningen</>
                      ) : (
                        <>Inga deltagare har definierats för detta alternativ</>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EnhancedParticipantsWidget;
